"use client";

import axios from "axios";
import {
  getActiveToken,
  getActiveEnvironment,
  setTokenForEnv,
  clearTokenForEnv,
  sessionReady,
} from "./auth-tokens";
import { ROUTES } from "../constants/routes";
import { triggerRotationGate } from "./rotation-gate";

const BASE_URL = process.env.NEXT_PUBLIC_API_URL || "https://api.emicashpay.com";

const BOOTSTRAP_PENDING_ENDPOINTS = [
  ROUTES.PROFILE,
  ROUTES.AGGREGATOR_BALANCE,
];

export function getAuthToken(): string | null {
  return getActiveToken();
}

export const axiosInstance = axios.create({
  baseURL: `${BASE_URL}/api/v1`,
  headers: { "Content-Type": "application/json" },
  timeout: 10000,
});

// Never force-reload a page the user is already on — location.replace() to
// the current URL still triggers a full reload, which would re-run this same
// failure path on mount and loop forever (e.g. a stray authenticated request
// firing on /login after logout).
function redirectToLogin(): void {
  if (typeof window === "undefined") return;
  if (window.location.pathname === "/login") return;
  window.location.replace("/login");
}

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onRefreshComplete = (token: string) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

axiosInstance.interceptors.request.use(
  async (config) => {
    await sessionReady;
    const token = getActiveToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 403) {
      const url: string = originalRequest?.url ?? "";
      if (BOOTSTRAP_PENDING_ENDPOINTS.some((ep) => url.includes(ep))) {
        triggerRotationGate();
        return new Promise(() => {});
      }
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(axiosInstance(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const env = getActiveEnvironment();

      try {
        const res = await fetch("/api/auth/refresh", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ env }),
        });

        const data = await res.json();

        if (res.ok && data.access_token) {
          setTokenForEnv(env, data.access_token);
          onRefreshComplete(data.access_token);
          isRefreshing = false;
          originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
          return axiosInstance(originalRequest);
        } else {
          isRefreshing = false;
          refreshSubscribers = [];
          clearTokenForEnv(env);
          redirectToLogin();
          return Promise.reject(error);
        }
      } catch {
        isRefreshing = false;
        refreshSubscribers = [];
        clearTokenForEnv(env);
        redirectToLogin();
      }
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
